// Copyright 2026. WebPros International GmbH. All rights reserved.

import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import { beforeAppLoading } from 'common/services/AppLoadingService';
import AdminApp from 'admin/core/App';
import ClientApp from 'client/core/App';
import {
    isAdminSite,
    isProduction,
} from 'common/helpers/core';
import { store as AdminStore } from 'admin/core/store';
import { store as ClientStore } from 'client/core/store';
import { setUpInterceptors } from 'common/services/NetworkService';
import { initMixpanel } from 'common/analytics/mixpanel';

const App = () => isAdminSite() ? <AdminApp /> : <ClientApp />;

const renderApp = () => {
    isAdminSite() ? setUpInterceptors(AdminStore) : setUpInterceptors(ClientStore);
    const container = document.getElementById('root');
    const root = createRoot(container!);
    root.render(<App />);
};

if (isProduction()) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        // Sentry v11 collects everything unless told otherwise. Keep the v10 no-PII posture
        // Structure from the migration guide: https://github.com/getsentry/sentry-javascript/blob/7f13c61336918fd727f473faa341b9a24f23718e/MIGRATION.md?plain=1#L208-L230
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            },
            httpBodies: [],
            urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            queues: false,
            graphQL: { document: false, variables: false },
        },
        ignoreErrors: [
            /getBoundingClientRect+.*/,
            /Request failed with status code 401+.*/,
            /Request failed with status code 404+.*/,
            /Request failed with status code 422+.*/,
        ],
    });

    let mixpanelInitialized = false;

    type AppStore = typeof AdminStore | typeof ClientStore;

    const initMixpanelFromStore = (store: AppStore): boolean => {
        const state = store.getState();
        const cluster = state.app.meta.cluster;
        const userId = state.auth.user?.id;
        const isAuthenticated = state.auth.isAuth;

        if (cluster && userId && isAuthenticated && !mixpanelInitialized) {
            initMixpanel(cluster, userId);
            mixpanelInitialized = true;
            return true;
        }
        return false;
    };

    AdminStore.subscribe(() => {
        if (mixpanelInitialized) { return; }
        initMixpanelFromStore(AdminStore);
    });

    ClientStore.subscribe(() => {
        if (mixpanelInitialized) { return; }
        initMixpanelFromStore(ClientStore);
    });
}

// We should wait until version is updated 'cause we get race condition when user try to use auth_by_link route
// (SVM-2622)
beforeAppLoading().then(renderApp);
